import { Skeleton } from '@/components/ui/skeleton'

/**
 * Usage against one effective limit (§17).
 *
 * Effective limit = `coalesce(account override, plan limit)`. When the two
 * differ the override is called out, because "why is my ceiling not what the
 * plan says" is otherwise unanswerable from the console.
 */
export function LimitMeter({
  label,
  hint,
  used,
  max,
  planMax,
  loading,
}: {
  label: string
  hint?: string
  used: number | undefined
  max: number | undefined
  planMax?: number | undefined
  loading: boolean
}) {
  const ratio = used !== undefined && max !== undefined && max > 0 ? Math.min(used / max, 1) : 0
  const tone =
    ratio >= 1
      ? 'var(--destructive)'
      : ratio >= 0.8
        ? 'color-mix(in oklch, var(--accent) 80%, var(--destructive))'
        : 'var(--primary)'
  const overridden = planMax !== undefined && max !== undefined && planMax !== max

  return (
    <div className="space-y-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        {loading ? (
          <Skeleton className="h-4 w-14" />
        ) : (
          <span className="font-mono text-sm text-foreground tabular">
            {used ?? '—'}
            <span className="text-muted-foreground"> / {max ?? '—'}</span>
          </span>
        )}
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-label={label}
        aria-valuenow={used ?? 0}
        aria-valuemin={0}
        aria-valuemax={max ?? 0}
      >
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${String(Math.round(ratio * 100))}%`, backgroundColor: tone }}
        />
      </div>
      {overridden ? (
        <p className="text-xs text-muted-foreground">
          Overridden for this account — the plan default is{' '}
          <span className="font-mono tabular">{planMax}</span>.
        </p>
      ) : hint === undefined ? null : (
        <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  )
}
